"use client";

import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { Clock, Loader2 } from "lucide-react";

type StaffOption = {
  id: string;
  userName: string | null;
  userEmail: string;
};

export type ShopPosShiftGateProps = {
  posStaffSelectId: string;
  selectedPosShopStaffId: string | null;
  onSelectStaff: (staffId: string | null) => void;
  staffOptions: StaffOption[] | undefined;
  staffLoading: boolean;
  shiftLoading: boolean;
  shiftError: boolean;
  openPending: boolean;
  onOpenShift: () => void;
};

/** ໜ້າບັງຄັບເປີດກະກ່ອນໃຊ້ POS — ad-hoc (ບໍ່ຕ້ອງມີ template) */
export function ShopPosShiftGate({
  posStaffSelectId,
  selectedPosShopStaffId,
  onSelectStaff,
  staffOptions,
  staffLoading,
  shiftLoading,
  shiftError,
  openPending,
  onOpenShift,
}: ShopPosShiftGateProps) {
  const canOpen =
    Boolean(selectedPosShopStaffId) &&
    !staffLoading &&
    !shiftLoading &&
    !shiftError &&
    !openPending;

  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col pt-3 sm:pt-4"
      role="alert"
    >
      <div className="relative flex min-h-[min(82dvh,calc(100dvh-10rem))] w-full flex-1 flex-col overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-[0_24px_80px_-24px_color-mix(in_oklab,var(--foreground)_25%,transparent)]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,color-mix(in_oklab,var(--primary)_18%,transparent),transparent_55%)]"
        />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/35 to-transparent" />
        <div className="relative flex min-h-0 flex-1 flex-col px-4 py-8 sm:px-8 sm:py-10 md:px-12 md:py-12 lg:px-14 lg:py-14">
          <div className="mx-auto grid w-full max-w-none flex-1 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] lg:gap-12 xl:gap-16">
            <div className="flex flex-col justify-center gap-5 lg:pr-4">
              <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary shadow-inner sm:size-20">
                <Clock className="size-9 sm:size-11" aria-hidden />
              </div>
              <div>
                <p className="font-mono text-[10px] text-primary uppercase tracking-[0.2em] sm:text-xs">
                  POS · ກະວຽກ
                </p>
                <h2 className="mt-2 font-bold font-heading text-2xl text-card-foreground tracking-tight sm:text-3xl md:text-4xl">
                  ຕ້ອງເປີດກະກ່ອນຂາຍ
                </h2>
                <p className="mt-4 max-w-xl text-base text-muted-foreground leading-relaxed sm:text-lg">
                  ເລືອກພະນັກງານ ແລ້ວກົດເປີດກະ — ບໍ່ຕ້ອງຕັ້ງແມ່ແບບກະກ່ອນ
                </p>
              </div>
            </div>
            <div className="flex min-h-0 flex-col justify-center">
              <div className="rounded-xl border border-border/80 bg-background/85 p-5 shadow-sm backdrop-blur-md sm:p-7 md:p-8">
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label
                      className="text-card-foreground text-sm"
                      htmlFor={posStaffSelectId}
                    >
                      ພະນັກງານ
                    </Label>
                    <Select
                      value={selectedPosShopStaffId ?? ""}
                      onValueChange={(v) =>
                        onSelectStaff(v === "" ? null : v)
                      }
                    >
                      <SelectTrigger
                        id={posStaffSelectId}
                        className="h-12 w-full text-base"
                      >
                        <SelectValue placeholder="ເລືອກພະນັກງານ" />
                      </SelectTrigger>
                      <SelectContent>
                        {(staffOptions ?? []).map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.userName?.trim() || s.userEmail}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-h-[4.5rem] border-border border-t pt-5">
                    {staffLoading ? (
                      <div className="flex items-center gap-3 text-muted-foreground text-sm sm:text-base">
                        <Loader2
                          className="size-5 shrink-0 animate-spin text-primary"
                          aria-hidden
                        />
                        ກຳລັງໂຫຼດລາຍຊື່ພະນັກງານ…
                      </div>
                    ) : (staffOptions?.length ?? 0) === 0 ? (
                      <p className="text-muted-foreground text-sm sm:text-base">
                        ບໍ່ມີພະນັກງານໃຫ້ເລືອກ (ສາຂາ/ສິດ) — ຕິດຕໍ່ຜູ້ຄຸມຮ້ານ
                      </p>
                    ) : !selectedPosShopStaffId ? (
                      <p className="text-muted-foreground text-sm sm:text-base">
                        ເລືອກພະນັກງານກ່ອນ ແລ້ວຈຶ່ງເປີດກະໄດ້
                      </p>
                    ) : shiftLoading ? (
                      <div className="flex items-center gap-3 text-muted-foreground text-sm sm:text-base">
                        <Loader2
                          className="size-5 shrink-0 animate-spin text-primary"
                          aria-hidden
                        />
                        ກຳລັງໂຫຼດສະຖານະກະ…
                      </div>
                    ) : shiftError ? (
                      <p className="text-destructive text-sm sm:text-base">
                        ໂຫຼດກະບໍ່ສຳເລັດ — ລອງໂຫຼດໜ້າຄືນ
                      </p>
                    ) : (
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <p className="max-w-md text-card-foreground text-sm leading-snug sm:text-base">
                          ກົດເປີດກະເພື່ອເລີ່ມຂາຍ — ປິດກະເມື່ອຈົບວຽກ
                        </p>
                        <Button
                          type="button"
                          className="h-12 w-full shrink-0 px-8 font-semibold sm:w-auto sm:min-w-[10rem]"
                          disabled={!canOpen}
                          onClick={onOpenShift}
                        >
                          {openPending ? (
                            <>
                              <Loader2 className="mr-2 size-5 animate-spin" />
                              ກຳລັງເປີດ…
                            </>
                          ) : (
                            "ເປີດກະ"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
